<script setup lang="ts">
  import { computed } from 'vue';

  const props = defineProps<{
    planName: string;
    statusText: string;
    statusColor: string;
    currentAccessName: string;
    planExpiresAt: string | null;
    nextBillingAt: string | null;
    cancelAtPeriodEnd: boolean;
    pendingConfirmation: boolean;
    renewalPending: boolean;
    primaryActionText?: string;
    disableActions?: boolean;
    showCustomerPortal?: boolean;
    canCancel: boolean;
  }>();

  defineEmits<{
    (e: 'change-plan'): void;
    (e: 'cancel'): void;
    (e: 'refresh'): void;
    (e: 'open-portal'): void;
  }>();

  function safeDateLabel(iso: string | null) {
    if (!iso) return null;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString();
  }

  const planExpiresLabel = computed(() => safeDateLabel(props.planExpiresAt));
  const nextBillingLabel = computed(() => safeDateLabel(props.nextBillingAt));
  const primaryActionLabel = computed(
    () => props.primaryActionText ?? 'Cambiar plan'
  );

  // Auto-renewing subscriptions show the exact period end (next billing date)
  // as the access limit. While the renewal is processing, the guaranteed
  // access horizon is planExpiresAt (period end + grace).
  const accessUntilValue = computed(() => {
    if (props.renewalPending) return planExpiresLabel.value ?? '—';
    return nextBillingLabel.value ?? planExpiresLabel.value ?? '—';
  });

  const nextBillingValue = computed(() => {
    if (props.renewalPending) return 'En proceso';
    return nextBillingLabel.value ?? '—';
  });

  const tierClass = computed(() => {
    const combined = `${props.planName} ${props.currentAccessName}`.toLowerCase();
    if (combined.includes('pro'))
      return 'billing-subscription-manage-card--pro';
    if (combined.includes('plus'))
      return 'billing-subscription-manage-card--plus';
    return 'billing-subscription-manage-card--free';
  });

  const statusTone = computed(() => {
    const color = (props.statusColor ?? '').toLowerCase();
    if (color === 'success') return 'success';
    if (color === 'warning') return 'warning';
    if (color === 'info') return 'info';
    return 'neutral';
  });

  const statusIcon = computed(() => {
    if (statusTone.value === 'success') return 'mdi-check-circle';
    if (statusTone.value === 'warning') return 'mdi-alert';
    if (statusTone.value === 'info') return 'mdi-information';
    return 'mdi-circle-outline';
  });

  const cancelNote = computed(() => {
    if (!props.cancelAtPeriodEnd) return null;
    if (planExpiresLabel.value) {
      return `La suscripción está cancelada. Mantendrá el acceso hasta ${planExpiresLabel.value}.`;
    }
    return 'La suscripción está cancelada. Mantendrá el acceso hasta el fin del período actual.';
  });
</script>

<template>
  <v-card
    class="billing-subscription-manage-card"
    :class="tierClass"
    elevation="0"
  >
    <div class="billing-subscription-manage-card__header">
      <div class="billing-subscription-manage-card__header-top">
        <div class="billing-subscription-manage-card__title">
          <div class="billing-subscription-manage-card__eyebrow">
            Su suscripción
          </div>
          <div class="billing-subscription-manage-card__plan">
            {{ planName }}
          </div>
        </div>

        <div class="billing-subscription-manage-card__header-actions">
          <v-btn
            v-if="showCustomerPortal"
            class="billing-subscription-manage-card__portal-btn"
            small
            depressed
            :disabled="disableActions"
            @click="$emit('open-portal')"
          >
            <v-icon left small>mdi-credit-card-outline</v-icon>
            Administrar facturación
          </v-btn>

          <div
            class="billing-subscription-manage-card__status"
            :class="`billing-subscription-manage-card__status--${statusTone}`"
          >
            <v-icon
              class="billing-subscription-manage-card__status-icon"
              size="18"
            >
              {{ statusIcon }}
            </v-icon>
            <span class="billing-subscription-manage-card__status-text">
              {{ statusText }}
            </span>
          </div>
        </div>
      </div>

      <div class="billing-subscription-manage-card__subtitle">
        <v-icon
          class="billing-subscription-manage-card__subtitle-icon"
          size="16"
        >
          mdi-shield-check
        </v-icon>
        <span>Administre su plan en cualquier momento.</span>
      </div>
    </div>

    <v-card-text class="billing-subscription-manage-card__body">
      <div class="billing-subscription-manage-card__facts">
        <div class="billing-subscription-manage-card__fact">
          <div class="billing-subscription-manage-card__fact-icon">
            <v-icon size="18">mdi-crown-outline</v-icon>
          </div>
          <div class="billing-subscription-manage-card__fact-label">
            Acceso actual
          </div>
          <div class="billing-subscription-manage-card__fact-value">
            {{ currentAccessName }}
          </div>
        </div>

        <div class="billing-subscription-manage-card__fact">
          <div class="billing-subscription-manage-card__fact-icon">
            <v-icon size="18">mdi-calendar-check</v-icon>
          </div>
          <div class="billing-subscription-manage-card__fact-label">
            Acceso hasta
          </div>
          <div class="billing-subscription-manage-card__fact-value">
            {{ accessUntilValue }}
          </div>
        </div>

        <div class="billing-subscription-manage-card__fact">
          <div class="billing-subscription-manage-card__fact-icon">
            <v-icon size="18">mdi-credit-card-clock-outline</v-icon>
          </div>
          <div class="billing-subscription-manage-card__fact-label">
            Próximo cobro
          </div>
          <div class="billing-subscription-manage-card__fact-value">
            {{ nextBillingValue }}
          </div>
        </div>
      </div>

      <div
        v-if="renewalPending"
        class="billing-subscription-manage-card__banner billing-subscription-manage-card__banner--pending"
      >
        <div class="billing-subscription-manage-card__banner-content">
          <v-icon
            class="billing-subscription-manage-card__banner-icon"
            size="20"
          >
            mdi-autorenew
          </v-icon>
          <div class="billing-subscription-manage-card__banner-text">
            <div class="billing-subscription-manage-card__banner-title">
              Renovación en proceso
            </div>
            <div class="billing-subscription-manage-card__banner-subtitle">
              Estamos procesando el cobro de su renovación. Su plan sigue activo
              y no tiene que hacer nada.
            </div>
          </div>
        </div>

        <v-btn
          class="billing-subscription-manage-card__banner-action"
          small
          depressed
          :disabled="disableActions"
          @click="$emit('refresh')"
        >
          <v-icon left small>mdi-refresh</v-icon>
          Actualizar
        </v-btn>
      </div>

      <div
        v-if="pendingConfirmation"
        class="billing-subscription-manage-card__banner billing-subscription-manage-card__banner--pending"
      >
        <div class="billing-subscription-manage-card__banner-content">
          <v-icon
            class="billing-subscription-manage-card__banner-icon"
            size="20"
          >
            mdi-timer-sand
          </v-icon>
          <div class="billing-subscription-manage-card__banner-text">
            <div class="billing-subscription-manage-card__banner-title">
              Confirmando el pago
            </div>
            <div class="billing-subscription-manage-card__banner-subtitle">
              Puede tardar unos segundos. Si ya pagó, actualice el estado.
            </div>
          </div>
        </div>

        <v-btn
          class="billing-subscription-manage-card__banner-action"
          small
          depressed
          :disabled="disableActions"
          @click="$emit('refresh')"
        >
          <v-icon left small>mdi-refresh</v-icon>
          Actualizar
        </v-btn>
      </div>

      <div
        v-if="cancelNote"
        class="billing-subscription-manage-card__banner billing-subscription-manage-card__banner--info"
      >
        <div class="billing-subscription-manage-card__banner-content">
          <v-icon
            class="billing-subscription-manage-card__banner-icon"
            size="20"
          >
            mdi-information-outline
          </v-icon>
          <div class="billing-subscription-manage-card__banner-text">
            <div class="billing-subscription-manage-card__banner-title">
              Suscripción cancelada
            </div>
            <div class="billing-subscription-manage-card__banner-subtitle">
              {{ cancelNote }}
            </div>
          </div>
        </div>
      </div>

      <div class="billing-subscription-manage-card__fineprint">
        Puede cambiar de plan o cancelar en cualquier momento.
      </div>
    </v-card-text>

    <div class="billing-subscription-manage-card__actions">
      <v-btn
        class="billing-subscription-manage-card__btn billing-subscription-manage-card__btn--primary"
        large
        :disabled="disableActions"
        @click="$emit('change-plan')"
      >
        <v-icon left>mdi-swap-horizontal</v-icon>
        {{ primaryActionLabel }}
      </v-btn>

      <v-btn
        class="billing-subscription-manage-card__btn billing-subscription-manage-card__btn--secondary"
        large
        :disabled="disableActions || !canCancel"
        @click="$emit('cancel')"
      >
        <v-icon left>mdi-close-circle-outline</v-icon>
        Cancelar suscripción
      </v-btn>
    </div>
  </v-card>
</template>

<style scoped lang="scss">
  .billing-subscription-manage-card {
    position: relative;
    border-radius: 18px;
    overflow: hidden;
    border: 1px solid rgba(15, 23, 42, 0.08);
    box-shadow: 0 18px 38px rgba(15, 23, 42, 0.1);

    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 0;
      height: 4px;
      width: 100%;
      background: rgba(15, 23, 42, 0.2);
    }

    &--plus::before {
      background: linear-gradient(135deg, #ff6f00, #ff9800);
    }

    &--pro::before {
      background: linear-gradient(135deg, #ff6f00, #ffd54f);
    }

    &--free::before {
      background: rgba(148, 163, 184, 0.9);
    }

    &__header {
      padding: 18px 18px 14px;
      background: linear-gradient(
        180deg,
        rgba(255, 111, 0, 0.08),
        rgba(255, 255, 255, 0)
      );
    }

    &--pro &__header {
      background: linear-gradient(
        180deg,
        rgba(255, 111, 0, 0.12),
        rgba(255, 255, 255, 0)
      );
    }

    &__header-top {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 12px;
      flex-wrap: wrap;
    }

    &__header-actions {
      display: inline-flex;
      align-items: center;
      justify-content: flex-end;
      gap: 10px;
      flex-wrap: wrap;
    }

    &__eyebrow {
      font-size: 12px;
      font-weight: 900;
      letter-spacing: 0.2px;
      color: rgba(15, 23, 42, 0.6);
      margin-bottom: 4px;
    }

    &__portal-btn {
      border-radius: 999px;
      height: 32px;
      padding: 0 12px;
      text-transform: none;
      font-weight: 900;
      letter-spacing: 0.2px;
      background: rgba(255, 255, 255, 0.92) !important;
      color: rgba(15, 23, 42, 0.86) !important;
      border: 1px solid rgba(15, 23, 42, 0.16) !important;
      box-shadow: none !important;

      &[disabled] {
        box-shadow: none !important;
        background: rgba(15, 23, 42, 0.12) !important;
        color: rgba(15, 23, 42, 0.46) !important;
        border-color: transparent !important;
      }
    }

    &__plan {
      font-size: 22px;
      font-weight: 900;
      letter-spacing: -0.3px;
      color: rgba(15, 23, 42, 0.94);
      line-height: 1.15;
    }

    &__status {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      padding: 7px 12px;
      border-radius: 999px;
      font-size: 12px;
      font-weight: 900;
      letter-spacing: 0.2px;
      border: 1px solid transparent;

      &--success {
        background: rgba(34, 197, 94, 0.12);
        color: rgba(20, 83, 45, 0.98);
        border-color: rgba(34, 197, 94, 0.18);
      }

      &--warning {
        background: rgba(245, 158, 11, 0.14);
        color: rgba(120, 53, 15, 0.98);
        border-color: rgba(245, 158, 11, 0.22);
      }

      &--info {
        background: rgba(59, 130, 246, 0.12);
        color: rgba(30, 58, 138, 0.98);
        border-color: rgba(59, 130, 246, 0.18);
      }

      &--neutral {
        background: rgba(148, 163, 184, 0.14);
        color: rgba(15, 23, 42, 0.78);
        border-color: rgba(148, 163, 184, 0.26);
      }
    }

    &__status-icon {
      opacity: 0.95;
    }

    &__subtitle {
      margin-top: 12px;
      display: inline-flex;
      align-items: center;
      gap: 8px;
      color: rgba(15, 23, 42, 0.58);
      font-size: 12px;
      font-weight: 700;
    }

    &__subtitle-icon {
      color: rgba(34, 197, 94, 0.9);
    }

    &__body {
      padding: 14px 18px 6px !important;
    }

    &__facts {
      display: grid;
      grid-template-columns: 1fr;
      gap: 10px;

      @media (min-width: 600px) {
        grid-template-columns: repeat(3, 1fr);
      }
    }

    &__fact {
      border: 1px solid rgba(15, 23, 42, 0.08);
      border-radius: 14px;
      padding: 12px 12px;
      background: rgba(255, 255, 255, 0.9);
    }

    &__fact-icon {
      width: 34px;
      height: 34px;
      border-radius: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: rgba(15, 23, 42, 0.06);
      margin-bottom: 10px;
      color: rgba(15, 23, 42, 0.72);
    }

    &--plus &__fact-icon,
    &--pro &__fact-icon {
      background: rgba(255, 111, 0, 0.1);
      color: rgba(120, 53, 15, 0.98);
    }

    &__fact-label {
      font-size: 12px;
      font-weight: 900;
      color: rgba(15, 23, 42, 0.58);
      margin-bottom: 4px;
      letter-spacing: 0.15px;
    }

    &__fact-value {
      font-size: 14px;
      font-weight: 900;
      color: rgba(15, 23, 42, 0.88);
      letter-spacing: -0.1px;
    }

    &__banner {
      margin-top: 14px;
      padding: 12px 12px;
      border-radius: 14px;
      border: 1px solid rgba(15, 23, 42, 0.08);
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      flex-wrap: wrap;

      &--pending {
        background: rgba(59, 130, 246, 0.08);
        border-color: rgba(59, 130, 246, 0.16);
      }

      &--info {
        background: rgba(148, 163, 184, 0.12);
        border-color: rgba(148, 163, 184, 0.22);
      }
    }

    &__banner-content {
      display: flex;
      align-items: flex-start;
      gap: 10px;
      min-width: 220px;
      flex: 1;
    }

    &__banner-icon {
      color: rgba(30, 58, 138, 0.9);
      margin-top: 1px;
    }

    &__banner--info &__banner-icon {
      color: rgba(15, 23, 42, 0.72);
    }

    &__banner-title {
      font-size: 13px;
      font-weight: 900;
      color: rgba(15, 23, 42, 0.9);
      margin-bottom: 2px;
    }

    &__banner-subtitle {
      font-size: 12px;
      color: rgba(15, 23, 42, 0.62);
      font-weight: 700;
      line-height: 1.35;
    }

    &__banner-action {
      border-radius: 12px;
      text-transform: none;
      font-weight: 900;
      letter-spacing: 0.2px;
      background: rgba(15, 23, 42, 0.92) !important;
      color: #fff !important;
      box-shadow: 0 12px 24px rgba(15, 23, 42, 0.16) !important;

      &[disabled] {
        box-shadow: none !important;
        background: rgba(15, 23, 42, 0.14) !important;
        color: rgba(15, 23, 42, 0.5) !important;
      }
    }

    &__fineprint {
      margin-top: 12px;
      font-size: 12px;
      color: rgba(15, 23, 42, 0.56);
      font-weight: 700;
    }

    &__actions {
      padding: 16px 18px 18px;
      display: grid;
      gap: 10px;

      @media (min-width: 600px) {
        grid-template-columns: 1fr 1fr;
      }
    }

    &__btn {
      border-radius: 14px;
      height: 50px;
      font-weight: 900;
      letter-spacing: 0.2px;
      text-transform: none;

      &--primary {
        background: rgba(15, 23, 42, 0.92) !important;
        color: #fff !important;
        box-shadow: 0 18px 34px rgba(15, 23, 42, 0.18) !important;
      }

      &--secondary {
        background: rgba(255, 255, 255, 0.9) !important;
        color: rgba(15, 23, 42, 0.86) !important;
        border: 1px solid rgba(15, 23, 42, 0.16) !important;
        box-shadow: none !important;
      }

      &[disabled] {
        box-shadow: none !important;
        background: rgba(15, 23, 42, 0.12) !important;
        color: rgba(15, 23, 42, 0.46) !important;
        border-color: transparent !important;
      }
    }
  }
</style>
